/**
 * Satış potansiyeli arayüz metinleri (v4.6; sözleşme `docs/spec/castinflow/satis-potansiyeli.md` § 5). Görünen her metin Türkçe
 * ve "siz" hitabıyladır. Yüzde bir TAHMİNDİR: ölçülmüş başarı olasılığı gibi sunulmaz, durum değiştirmez.
 */
import { SALES_ASSESSMENT_NOTICE } from '@castinflow/shared';

export const potentialText = {
  title: 'Satış potansiyeli',
  notice: SALES_ASSESSMENT_NOTICE,
  requiredBadge: 'Zorunlu',
  optionalBadge: 'İsteğe bağlı',
  optionalHint: 'Boş bırakabilirsiniz.',

  // Değerlendirme alanı
  subject: 'Değerlendirilen kayıt',
  subjectChoose: 'Birden çok kayıt değerlendirilebilir; hangisi için tahmin verdiğinizi seçin.',
  subjectRequired: 'Değerlendirdiğiniz kaydı seçin.',
  subjectOne: (title: string, type: string) => `Değerlendirilen kayıt: ${title} (${type})`,
  percent: 'Potansiyel (%)',
  percentHelp: '0 ile 100 arasında bir tam sayı girin.',
  percentInvalid: 'Yüzde 0 ile 100 arasında bir tam sayı olmalı.',
  percentMissing: 'Yüzde girin ya da "Değerlendirilemedi"yi seçip gerekçe yazın.',
  percentMissingNoSkip: 'Yüzde girin.',
  notAssessable: 'Değerlendirilemedi',
  notAssessableHelp: 'Tahmin için yeterli bilgi yoksa işaretleyin ve nedenini yazın.',
  reason: 'Gerekçe',
  reasonPlaceholder: 'Ör. Karar verici henüz belli değil',
  reasonInvalid: 'Gerekçe en az 3, en çok 500 karakter olmalı.',
  guide: 'Değerlendirme rehberi',
  guideIntro:
    'Yüzdeyi verirken şu soruları düşünün. İşaretlemek isteğe bağlıdır ve kayıtla birlikte saklanır.',
  guideQuestions: {
    need: 'Müşterinin bu ürüne ya da hizmete gerçek bir ihtiyacı var mı?',
    budget: 'Bütçesi teklifinizle uyumlu mu?',
    decisionMaker: 'Karar verecek kişiyle görüşüyor musunuz?',
    timing: 'Satın alma için bir zaman belli mi?',
    nextStep: 'Bir sonraki adım üzerinde birlikte karar verdiniz mi?',
  },
  criterionOptions: { unknown: 'Belirsiz', yes: 'Evet', no: 'Hayır' },
  current: {
    none: 'Değerlendirme yok',
    notAssessable: 'Değerlendirilemedi',
    assessed: (p: number, day: string) => `Son değerlendirme: %${String(p)} · ${day}`,
    notAssessableOn: (day: string) => `Son değerlendirme: değerlendirilemedi · ${day}`,
  },
  subjectType: { lead: 'Aday', opportunity: 'Fırsat' },

  // Potansiyel gösterimi (PotentialValue / describePotential): değer + kaynak + temas günü ve yaşı; renk tek başına anlam taşımaz
  value: {
    neverAssessed: 'Hiç değerlendirilmedi',
    stale: 'Eski değerlendirme',
    staleHint: (days: number) =>
      `Son değerlendirme ${String(days)} günden eski; yeni temasta yeniden değerlendirin.`,
    source: { manual: 'Manuel', stage_default: 'Aşama varsayılanı', model: 'Otomatik skor' },
    sourceEstimate: (source: string) => `${source.toLocaleLowerCase('tr-TR')} tahmin`,
    age: (n: number) => (n <= 0 ? 'bugün' : n === 1 ? 'dün' : `${String(n)} gün önce`),
    lastAssessed: 'Son değerlendirme',
  },

  // Özet satırı ve geçmiş
  manual: '(manuel)',
  historyOpen: 'Geçmişi gör',
  historyTitle: 'Satış potansiyeli geçmişi',
  historyEmpty: 'Bu kayıt için henüz değerlendirme girilmedi.',
  historyFailed: 'Değerlendirme geçmişi alınamadı.',
  historyTruncated: 'En yeni 200 değerlendirme gösteriliyor.',
  historyCurrent: 'Güncel',
  historyFromLead: 'adayken',
  historyFromLeadHint: 'Bu değerlendirme fırsata dönüştürülmeden önce adayda girildi.',
  historyBy: (name: string) => `Değerlendiren: ${name}`,
  historyUnknownUser: 'Değerlendiren: kullanıcı bilgisi yok',
  historyActivity: 'Temas:',
  historyActivityHidden: 'Temas: görüntüleyemediğiniz ya da silinmiş aktivite',
  historyCriteria: 'Rehber işaretleri',
  historyAt: (day: string) => `Temas günü ${day}`,
  retry: 'Yeniden dene',

  // Aktivite kaydet (birleşik form)
  log: {
    title: 'Aktivite kaydet',
    intro: (record: string) => `Bu temas şu kayda bağlanır: ${record}`,
    sectionContact: 'Görüşme sonucu',
    kind: 'Tür',
    name: 'Konu',
    namePlaceholder: 'Ör. Teklif sonrası telefon görüşmesi',
    description: 'Not',
    descriptionPlaceholder: 'Görüşmede ne konuşuldu, karşı tarafın yanıtı ne oldu?',
    occurredAt: 'Gerçekleşme zamanı',
    occurredAtZone: (timeZone: string) =>
      `Saat çalışma alanının saat dilimine göredir (${timeZone}).`,
    occurredFuture: 'Gerçekleşme zamanı ileri bir an olamaz.',
    occurredInvalid: 'Geçerli bir tarih ve saat girin.',
    related: 'İlgili fırsatlar',
    relatedHelp:
      'Görüşme başka fırsatları da ilgilendiriyorsa ekleyin; değerlendirme bunlardan biri için de girilebilir.',
    moreDetails: 'Ek ayrıntılar',
    moreDetailsCount: (n: number) =>
      n === 1 ? '1 isteğe bağlı alan' : `${String(n)} isteğe bağlı alan`,
    customForbidden: 'Kaydedilmedi: işaretli alanları değiştirme yetkiniz yok; boş bırakın.',
    scopeLoading: 'Değerlendirme kuralı denetleniyor…',
    scopeFailed:
      'Değerlendirme kuralı alınamadı. Kaydettiğinizde kural yine denetlenir; gerekirse sizden değerlendirme istenir.',
    /**
     * A2: satış potansiyeli alanı neden yok (kapsam yanıtının `outOfScopeReason`'ı). Staging gözlemi: alan görünmeyince kusur
     * sanılıyordu — kural kısa ve açık söylenir (renk değil metin).
     */
    outOfScope: {
      disabled:
        'Satış potansiyeli değerlendirmesi bu çalışma alanında kapalı (Ayarlar › Satış › Potansiyel değerlendirmesi).',
      kind: 'Bu aktivite türünde satış potansiyeli sorulmuyor. Değerlendirme için çalışma alanının seçtiği türlerden birini (ör. Çağrı) seçin.',
      no_subject:
        'Satış potansiyeli yalnız açık bir aday ya da fırsat için girilir. Bu temasa açık aday/fırsat bağlı değil; ilgili fırsatı ekleyebilirsiniz.',
      not_writable:
        'Bağlı aday/fırsatı düzenleme yetkiniz olmadığından satış potansiyeli bu temasla girilemez.',
    } as Record<'disabled' | 'kind' | 'no_subject' | 'not_writable', string>,
    followUp: 'Sonraki takip',
    followUpIntro: 'Görev bu temasla aynı anda kaydedilir ve seçtiğiniz sorumluya atanır.',
    followUpToggle: 'Takip görevi oluştur',
    followUpName: 'Görev adı',
    followUpNamePlaceholder: 'Ör. Teklifi hatırlatmak için arayın',
    followUpDue: 'Son tarih',
    followUpDuePast: 'Son tarih bugünden önce olamaz.',
    followUpAssignee: 'Sorumlu',
    duePresets: { tomorrow: 'Yarın', in3: '3 gün sonra', in7: '1 hafta sonra' },
    duePresetsLabel: 'Hızlı tarih seçimi',
    submit: 'Kaydet',
    cancel: 'Vazgeç',
    saved: 'Aktivite kaydedildi',
    savedWithFollowUp: 'Aktivite ve takip görevi kaydedildi',
    replayed: 'Bu temas daha önce kaydedilmişti; yeni kayıt açılmadı.',
    invalidSummary: 'Kaydedilmedi: işaretli alanları düzeltin.',
    failed: 'Kaydedilemedi; bilgileri kontrol edip yeniden deneyin.',
  },

  // Tamamlama kapısı
  complete: {
    title: 'Aktiviteyi tamamla',
    intro: (name: string) => `"${name}" tamamlanacak.`,
    submit: 'Tamamla',
    cancel: 'Vazgeç',
    done: 'Aktivite tamamlandı',
    failed: 'Aktivite tamamlanamadı; yeniden deneyin.',
    checking: 'Değerlendirme kuralı denetleniyor…',
    blockedTitle: 'Değerlendirme bekleyen aktiviteler',
    blockedIntro: (n: number) =>
      `${String(n)} aktivite tamamlanmadı: bu çalışma alanında satış potansiyeli değerlendirmesi zorunlu. Her birini değerlendirip tamamlayabilirsiniz; diğer aktiviteler tamamlandı.`,
    blockedItem: 'Değerlendir ve tamamla',
    blockedUnknown: 'Bu sayfada görünmeyen aktivite',
    blockedClose: 'Kapat',
    blockedCount: (n: number) => `${String(n)} değerlendirme bekliyor`,
  },

  // Takip görevi (tek kayıt ve toplu)
  task: {
    title: 'Takip görevi ata',
    single: 'Görev ata',
    introOne: (record: string) => `Görev bu kayda bağlanır: ${record}`,
    introMany: (n: number) =>
      `Seçili ${String(n)} kaydın her biri için ayrı bir görev oluşturulur; aynı ad, son tarih ve sorumluyla.`,
    name: 'Görev',
    namePlaceholder: 'Ör. Teklif için geri dönüş yapın',
    assignee: 'Sorumlu',
    due: 'Son tarih',
    selectedTitle: (n: number) => `Görev atanacak ${String(n)} kayıt`,
    moreRecords: (n: number) => `ve ${String(n)} kayıt daha`,
    unnamed: 'Adı görüntülenemeyen kayıt',
    dueZone: (timeZone: string) => `Gün çalışma alanının saat dilimine göredir (${timeZone}).`,
    note: 'Not',
    cancel: 'Vazgeç',
    submitOne: 'Görevi ata',
    submitMany: (n: number) => `${String(n)} kayda görev ata`,
    createdOne: 'Görev atandı',
    createdMany: (n: number) => `${String(n)} kayda takip görevi atandı`,
    refused:
      'Görev atanamadı: seçimdeki kayıtlardan en az birine görev atama (yazma) yetkiniz yok ya da kayıt artık yok.',
    rateLimited: 'Kısa sürede çok fazla toplu işlem yapıldı; bir dakika sonra yeniden deneyin.',
    failed: 'Görev atanamadı; bilgileri kontrol edip yeniden deneyin.',
    nothingChanged: 'Hiçbir kayda görev eklenmedi.',
    tooMany: 'Tek seferde en çok 50 kayda görev atayabilirsiniz; seçimi azaltın.',
    bulkButton: 'Takip görevi ata',
  },

  // Satış takibi sayfası
  worklist: {
    title: 'Satış takibi',
    description:
      'Hangi adayla ve fırsatla ne zaman ilgilenmeniz gerektiğini gösterir. Her listenin sıralaması açıkça yazılıdır; gizli puan yoktur.',
    visibilityNote:
      'Son temas, sonraki takip ve görev sayıları yalnız görüntüleyebildiğiniz aktivitelerden hesaplanır.',
    tabsLabel: 'Satış takibi listeleri',
    tabShort: {
      priority: 'Öncelikli',
      unassessed: 'Bekleyen',
      overdue: 'Geciken',
      stale: 'Yeniden',
    },
    countSr: (n: string) => `, ${n} kayıt`,
    listDescription: {
      priority: 'Potansiyelini yüksek değerlendirdiğiniz açık adaylar ve fırsatlar.',
      unassessed: 'İlk temasta potansiyelini girin; değerlendirilen aday bu listeden çıkar.',
      overdue: 'Son tarihi geçmiş açık işi olan kayıtlar; önce bu işleri kapatın.',
      stale:
        'Son değerlendirmesi 30 günden eski kayıtlar; yeni bir görüşmede potansiyeli güncelleyin. Puan kendiliğinden değişmez.',
    },
    mine: 'Yalnız benim kayıtlarım',
    order: (text: string) => `Sıralama: ${text}`,
    threshold: (n: number) => `Eşik: potansiyel %${String(n)} ve üzeri.`,
    caption: (list: string) => `${list} listesi`,
    columns: {
      record: 'Kayıt',
      potential: 'Potansiyel',
      assessedAt: 'Son değerlendirme',
      lastContact: 'Son temas',
      nextFollowUp: 'Sonraki takip',
      owner: 'Sorumlu',
      tasks: 'Açık / gecikmiş görev',
    },
    unknown: 'Bilinmiyor',
    notAssessable: 'Değerlendirilemedi',
    noOwner: 'Atanmamış',
    none: '—',
    overdue: 'Gecikti',
    tasks: (open: number, overdue: number) => `${String(open)} açık · ${String(overdue)} gecikmiş`,
    tasksOpen: (n: number) => `${String(n)} açık`,
    tasksOverdue: (n: number) => `${String(n)} gecikmiş`,
    empty: {
      priority: 'Eşiğin üzerinde potansiyeli olan açık kayıt yok.',
      unassessed: 'Değerlendirme bekleyen aday yok.',
      overdue: 'Görüntüleyebildiğiniz geciken takip yok.',
      stale: 'Değerlendirmesi eskimiş açık kayıt yok.',
    },
    emptyHint: 'Tamamladığınız temaslarda potansiyel girdikçe liste dolar.',
    emptyHints: {
      priority:
        'Tamamladığınız temaslarda potansiyel girdikçe eşiğin üzerindeki kayıtlar burada sıralanır.',
      unassessed: 'Açık adaylarınızın hepsi en az bir kez değerlendirildi.',
      overdue: 'Son tarihi geçen açık iş olduğunda kayıt burada görünür.',
      stale: 'Değerlendirmesi 30 günü geçen kayıt olduğunda burada görünür.',
    },
    /** C1 (TI-016): satırdan doğrudan işlemler */
    actions: {
      label: (title: string) => `${title} için işlemler`,
      column: 'İşlem',
      logContact: 'Görüşme kaydet',
      logContactAria: (title: string) => `${title}: görüşme kaydet`,
      followUp: 'Takip planla',
      followUpAria: (title: string) => `${title}: takip planla`,
      call: (phone: string) => `Ara: ${phone}`,
      mail: (email: string) => `E-posta gönder: ${email}`,
      saved: 'Görüşme kaydedildi.',
      savedWithFollowUp: 'Görüşme ve takip görevi kaydedildi.',
      replayed: 'Bu görüşme zaten kaydedilmişti.',
      taskCreated: 'Takip görevi oluşturuldu.',
      staleHint: 'Değerlendirme eski',
    },
    loadFailed: 'Liste alınamadı.',
    loadFailedHint: 'Bağlantınızı kontrol edip yeniden deneyin.',
    retry: 'Yeniden dene',
    truncated: (n: number) => `İlk ${String(n)} kayıt gösteriliyor; listede daha fazla kayıt var.`,
  },

  // Liste süzgeci
  presetFilter: (n: number) => `Potansiyel ≥ %${String(n)}`,

  // Ayarlar
  settings: {
    tab: 'Potansiyel değerlendirmesi',
    intro:
      'Satış ekibinden, tamamladıkları temasta aday ya da fırsat için tahmini bir yüzde isteyebilirsiniz. Yüzde ölçülmüş başarı olasılığı değildir; kaydın durumunu değiştirmez.',
    sectionWhen: 'Ne zaman istenir',
    sectionWhenHelp: 'Hangi tamamlanan temaslarda değerlendirme istendiğini seçin.',
    sectionWhich: 'Hangi kayıtlar değerlendirilir',
    sectionWhichHelp: 'Değerlendirme yalnız açık adaylar ve açık fırsatlar için girilir.',
    sectionOptions: 'Seçenekler ve öncelik',
    readOnly: 'Bu ayarı yalnız ayar yönetme yetkisi olan kullanıcılar değiştirebilir.',
    mode: 'Değerlendirme',
    modeHelp: {
      off: 'Değerlendirme alanı gösterilmez.',
      optional: 'Alan gösterilir; kullanıcı boş bırakabilir.',
      required: 'Kapsamdaki aktivite, değerlendirme girilmeden tamamlanamaz.',
    },
    kinds: 'Aktivite türleri',
    kindsHelp: 'Bu türlerdeki aktiviteler tamamlanırken değerlendirme istenir.',
    kindsRequired: 'En az bir aktivite türü seçin.',
    includeLeads: 'Açık adaylar da değerlendirilsin',
    includeLeadsHelp:
      'Dönüştürülmemiş, niteliksiz ya da tekrar eden olarak işaretlenmemiş adaylar.',
    stages: 'Fırsat aşamaları',
    stagesAll: 'Bütün aşamalardaki açık fırsatlar',
    stagesSome: 'Yalnız seçili aşamalar',
    stagesRequired: 'En az bir aşama seçin.',
    stagesLoading: 'Aşamalar yükleniyor…',
    stagesInactive: 'pasif',
    allowNotAssessable: '"Değerlendirilemedi" seçeneğine izin verilsin',
    allowNotAssessableHelp:
      'Tahmin için bilgi yoksa kullanıcı gerekçe yazarak geçebilir; bu kayıt sıfır sayılmaz.',
    threshold: 'Öncelik eşiği (%)',
    thresholdHelp:
      '"Satış takibi" sayfasındaki öncelikli liste, potansiyeli bu değer ve üzerindeki kayıtları gösterir.',
    thresholdInvalid: 'Eşik 0 ile 100 arasında bir tam sayı olmalı.',
    save: 'Kaydet',
    saved: 'Potansiyel değerlendirmesi ayarı kaydedildi',
    conflict:
      'Ayar siz düzenlerken başka biri tarafından değiştirildi; güncel hâli yüklendi. Değişikliğinizi yeniden yapın.',
    failed: 'Ayar kaydedilemedi; bilgileri kontrol edip yeniden deneyin.',
    loadFailed: 'Ayar alınamadı.',
    isDefault: 'Bu çalışma alanında henüz kaydedilmedi; ürün varsayılanı (kapalı) geçerli.',
    lastUpdated: (when: string, name: string | null) =>
      name === null ? `Son değişiklik: ${when}` : `Son değişiklik: ${when} · ${name}`,
  },
};
